import { useId, useState } from "react";
import { CheckIcon, EyeIcon, EyeOffIcon } from "./icons";
import { FieldHint, FieldLabel, INPUT_CLASS, fieldClass } from "./Field";

/*
  Figma "Password field": a 44 × 44 show/hide toggle (eye-off ↔ eye) and an
  optional live rule under the field. The rule mark fills green once
  `ruleMet`; with `ruleError` (an unmet rule after submit) the rule and the
  outline turn red. Figma draws this field 44px tall on every screen size.
*/
const PasswordField = ({ label, rule, ruleMet = false, ruleError = false, error, id, className = "", ...inputProps }) => {
  const autoId = useId();
  const inputId = id || autoId;
  const [visible, setVisible] = useState(false);
  const tone = error || ruleError ? "error" : "default";
  const describedBy = [rule && `${inputId}-rule`, error && `${inputId}-hint`].filter(Boolean).join(" ");

  const ruleState = ruleError && !ruleMet ? "error" : ruleMet ? "met" : "idle";
  const markClass = {
    idle: "border-[1.5px] border-label-tertiary",
    met: "bg-status-success text-bg-base",
    error: "border-[1.5px] border-status-danger",
  }[ruleState];
  const ruleTextClass = {
    idle: "text-footnote text-label-secondary",
    met: "text-footnote-semibold text-status-success",
    error: "text-footnote text-status-danger",
  }[ruleState];

  return (
    <div className={`flex w-full flex-col gap-[6px] ${className}`}>
      {label && <FieldLabel htmlFor={inputId}>{label}</FieldLabel>}
      <div className={fieldClass({ tone, height: "h-[44px]", className: "gap-space-4 pl-space-16 pr-space-4" })}>
        <input
          id={inputId}
          type={visible ? "text" : "password"}
          aria-invalid={tone === "error" ? true : undefined}
          aria-describedby={describedBy || undefined}
          className={INPUT_CLASS}
          {...inputProps}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? "Hide password" : "Show password"}
          aria-pressed={visible}
          className="relative z-[1] flex size-[44px] shrink-0 items-center justify-center rounded-radius-md text-label-secondary outline-none focus-visible:shadow-[inset_0_0_0_2px_var(--color-focus-ring)]"
        >
          {visible ? <EyeIcon /> : <EyeOffIcon />}
        </button>
      </div>
      {rule && (
        <div id={`${inputId}-rule`} className="flex items-center gap-[6px]">
          <span className={`flex size-4 shrink-0 items-center justify-center rounded-radius-sm ${markClass}`}>
            {ruleState === "met" && <CheckIcon />}
          </span>
          <p className={`min-w-0 flex-1 ${ruleTextClass}`}>{rule}</p>
        </div>
      )}
      {error && (
        <FieldHint id={`${inputId}-hint`} tone="error">
          {error}
        </FieldHint>
      )}
    </div>
  );
};

export default PasswordField;
